import { useMemo } from 'react';
import type { SchemaProperty } from 'types/schema';
import type { SectionLayoutGroup } from '../../../utils/resolveSectionLayout';

/** Tab id of a docked section. */
export const sectionTabId = (sectionKey: string) => `section-${sectionKey}-tab`;

/** Tab id of a legacy `dock: true` property that sits outside every docked section. */
export const dockedPropertyTabId = (propertyName: string) => `docked-${propertyName}-tab`;

export interface UseEditorTabIndexArgs {
  /** Ids of the tabs rendered before the docked ones, in order. Only the ids are compared. */
  leadingTabIds: string[];
  /** Docked section groups, rendered as tabs after the leading tabs. */
  dockedGroups: SectionLayoutGroup[];
  /** Legacy docked properties, rendered as tabs after the docked sections. */
  dockedProperties: SchemaProperty[];
  /** The type's properties in schema order. */
  properties: SchemaProperty[];
}

export interface EditorTabIndex {
  /** Every tab id in render order. */
  tabIds: string[];
  /** Property names in schema order. */
  propertyOrder: string[];
  /** Tab index of each property edited outside the Properties tab (docked section or legacy docked tab). */
  tabIndexByProperty: Record<string, number>;
}

/**
 * The Sphere Editor's tab ids and the tab that edits each docked property. The tab objects are
 * rebuilt every render, so the leading ids are keyed as one string: the results keep their identity
 * until a tab is added, removed or reordered, or the docked layout or the type's properties change.
 */
export function useEditorTabIndex({ leadingTabIds, dockedGroups, dockedProperties, properties }: UseEditorTabIndexArgs): EditorTabIndex {
  const leadingKey = leadingTabIds.join('\n');

  const tabIds = useMemo(
    () => [
      ...(leadingKey ? leadingKey.split('\n') : []),
      ...dockedGroups.map((group) => sectionTabId(group.key)),
      ...dockedProperties.map((prop) => dockedPropertyTabId(prop.name)),
    ],
    [leadingKey, dockedGroups, dockedProperties]
  );

  const tabIndexByProperty = useMemo(() => {
    const byProperty: Record<string, number> = {};
    for (const group of dockedGroups) {
      const tabIndex = tabIds.indexOf(sectionTabId(group.key));
      for (const prop of group.allProperties) byProperty[prop.name] = tabIndex;
    }
    for (const prop of dockedProperties) byProperty[prop.name] = tabIds.indexOf(dockedPropertyTabId(prop.name));
    return byProperty;
  }, [tabIds, dockedGroups, dockedProperties]);

  const propertyOrder = useMemo(() => properties.map((prop) => prop.name), [properties]);

  return { tabIds, propertyOrder, tabIndexByProperty };
}
